<script>
// https://forum.vuejs.org/t/render-inside-iframe/6419
// https://jsfiddle.net/rzuno6kv/

import Vue from 'vue';
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';

import { CC_GATEWAYS, loadScript, sleep } from '@/utils';
import StripeService from '@/services/stripe';
// import sentryInitialize from '@/services/sentry';
import dtdInitialize from '@/services/double-the-donation';
import trackEvent from '@/services/event-tracking';
import store from '@/store';
import i18n from '@/services/i18n';
import '@/common';

import VueTelInput from 'vue-tel-input';

import nunito from '!!raw-loader!@/assets/fonts/nunito.css';
import css from '!!raw-loader!sass-loader!@/assets/css/checkout/index.scss';
import telInputCss from '!!raw-loader!vue-tel-input/dist/vue-tel-input.css';
import CheckoutLoader from '@/common/CheckoutLoader.vue';

Vue.use(VueTelInput);
// import { unparam } from '@/utils';

Vue.config.productionTip = false;
Vue.config.ignoredElements = [/^ion-/];

Vue.prototype.$trackEvent = trackEvent;

export default {
  render(h) {
    return h('iframe', {
      on: { load: this.renderChildren },
      attrs: {
        src: 'about:blank',
        name: 'double-checkout',
        title: 'Donation Widget',
        allowpaymentrequest: 'true',
        allow: 'payment',
        frameborder: 0,
        tabIndex: -1
      },
      style: this.iframeStyles
    });
  },

  data: () => ({
    iframeStyles: {
      position: 'absolute',
      height: '100%',
      width: '100%',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0
    },
    CC_GATEWAYS
  }),

  computed: {
    ...mapState('publicOrgSettings', {
      globalSettings: (state) => state.settings,
      chariotCid: (state) => state.chariotCid
    }),
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState('campaign', ['settings']),
    ...mapGetters('campaign', ['campaignLoaded'])
  },

  watch: {
    async campaignLoaded(loaded) {
      if (!loaded) {
        return;
      }

      if (this.settings.customQuestions.some((q) => q.type === 'date')) {
        this.loadDatePickerLibrary();
      }
    }
  },

  methods: {
    ...mapMutations(['SET_DATE_PICKER_LIBRARY_LOADED']),
    ...mapActions(['setWidgetOpen']),

    async renderChildren() {
      const children = this.$slots.default;
      const body = this.$el.contentDocument.body;
      const el = document.createElement('DIV'); // we will mount or nested app to this element
      body.appendChild(el);

      // load iframe scripts and styles
      const iframeHead = this.$el.contentDocument.head;
      loadScript('https://cdn.jsdelivr.net/npm/ionicons@latest/dist/ionicons/ionicons.esm.js', {
        target: iframeHead,
        type: 'module'
      });
      loadScript('https://cdn.jsdelivr.net/npm/ionicons@latest/dist/ionicons/ionicons.js', {
        target: iframeHead,
        nomodule: true
      });
      loadScript('https://www.googletagmanager.com/gtag/js?id=' + process.env.VUE_APP_ADMIN_GANALYTICS_ID, {
        target: iframeHead,
        type: null,
        isAsync: true
      });

      if (this.chariotCid) {
        loadScript('https://cdn.givechariot.com/chariot-connect.umd.js', {
          target: iframeHead,
          isAsync: true
        });
      }

      dtdInitialize(this.globalSettings.doubleTheDonation);
      const currentVueInstance = this; // reference to current vue instance
      const WidgetIframe = new Vue({
        name: 'WidgetIframe',
        store,
        i18n,
        //freezing to prevent unnessessary Reactifiation of vNodes
        data: { children: Object.freeze(children) },
        render(h) {
          if (currentVueInstance.campaignLoaded) {
            return h('div', this.children);
          } else {
            return h(CheckoutLoader);
          }
        }
      });

      // Mounted hooks measure the fixed footer to reserve space below the form.
      // Style the iframe first so those measurements use the final layout.
      this.applyCss();

      WidgetIframe.$mount(el); // mount into iframe
      this.WidgetIframe = WidgetIframe; // cache instance for later updates
    },
    applyCss() {
      const iframe = this.$el.contentDocument;
      const styleElement = document.createElement('style');
      styleElement.textContent = css + nunito + telInputCss;
      iframe.head.appendChild(styleElement);
    },
    closeOnEsc(event) {
      if (event.key === 'Escape') {
        this.setWidgetOpen(false);
      }
    },

    async loadDatePickerLibrary() {
      const iframeHead = this.$el.contentDocument.head;

      await Promise.all([
        loadScript('https://cdn.jsdelivr.net/npm/@shayaulman/vuejs-datepicker@1.6.3-fork.2/dist/vuejs-datepicker.min.js', {
          target: iframeHead,
          isAsync: true
        }).then(() => Vue.component('date-picker', this.$el.contentWindow.vuejsDatepicker)),

        loadScript('https://cdn.jsdelivr.net/npm/@shayaulman/vuejs-datepicker@1.6.3-fork.2/dist/locale/translations/en.js', {
          target: iframeHead
        }),

        loadScript('https://cdn.jsdelivr.net/npm/@shayaulman/vuejs-datepicker@1.6.3-fork.2/dist/locale/translations/he.js', {
          target: iframeHead
        })
      ]);

      await sleep(300);
      this.SET_DATE_PICKER_LIBRARY_LOADED(true);
    }
  },

  created() {
    this.$trackEvent({ action: 'opened' });
  },

  mounted() {
    const iframe = this.$el.contentDocument;
    iframe.addEventListener('keydown', (event) => this.closeOnEsc(event));
    document.addEventListener('keydown', (event) => this.closeOnEsc(event));
  },

  beforeUpdate() {
    if (!this.WidgetIframe) {
      return;
    }
    // freezing to prevent unnecessary reactifiation of vNodes
    this.WidgetIframe.children = Object.freeze(this.$slots.default);
  },

  destroyed() {
    StripeService.destroyStripeObject();
    this.$trackEvent({ action: 'closed' });
    this.SET_DATE_PICKER_LIBRARY_LOADED(false);
  }
};
</script>
